import React, { useState } from "react";
import { FiCopy, FiCheck } from "react-icons/fi";
import {
  reactCode,
  vueCode,
  nextjsCode,
  nuxtCode,
  angularTsCode,
  svelteCode,
  htmlJsCode,
  wordpressCode,
  shopifyCode,
  phpCode,
  reactNativeCode,
  flutterCode,
  androidCode,
  iosCode,
  MOBILE_NOTES,
} from "../Documentation/widgets/IntegrationCodes";

// Embed-install picker used in the setup wizard + admin Integration tab.
// Flow: pick platform -> stack -> a few minimal options (theme) -> Generate.
// Kept intentionally minimal for now; richer options come later.

// `selfContained` snippets already include the widget.js loader.
const WEB = [
  { key: "react", label: "React", code: reactCode },
  { key: "nextjs", label: "Next.js", code: nextjsCode },
  { key: "vue", label: "Vue", code: vueCode },
  { key: "nuxt", label: "Nuxt", code: nuxtCode },
  { key: "angular", label: "Angular", code: angularTsCode },
  { key: "svelte", label: "Svelte", code: svelteCode },
  { key: "htmljs", label: "HTML / JS", code: htmlJsCode },
  { key: "wordpress", label: "WordPress", code: wordpressCode, selfContained: true },
  { key: "shopify", label: "Shopify", code: shopifyCode, selfContained: true },
  { key: "php", label: "PHP", code: phpCode },
];

const MOBILE = [
  { key: "reactnative", label: "React Native", code: reactNativeCode },
  { key: "flutter", label: "Flutter", code: flutterCode },
  { key: "android", label: "Android", code: androidCode },
  { key: "ios", label: "iOS", code: iosCode },
];

const loaderSnippet = (apiKey) =>
  `<script async src="https://cdn.channels.social/widget.js?apiKey=${apiKey}"></script>`;

// --- small UI helpers ---------------------------------------------------------

const Field = ({ label, children }) => (
  <div>
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
      {label}
    </p>
    {children}
  </div>
);

// segmented radio-style control
const Pills = ({ options, value, onChange }) => (
  <div className="inline-flex w-max rounded-lg border border-theme-chatDivider p-1">
    {options.map((o) => (
      <button
        key={o.v}
        onClick={() => onChange(o.v)}
        className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
          value === o.v
            ? "bg-theme-accent text-theme-accentText"
            : "text-theme-primaryText hover:bg-theme-tertiaryBackground"
        }`}
      >
        {o.l}
      </button>
    ))}
  </div>
);

const CodeBlock = ({ title, code }) => {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  return (
    <div>
      {title && (
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
          {title}
        </p>
      )}
      <div className="relative">
        <pre className="overflow-x-auto rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground p-3 pr-16 text-xs leading-relaxed text-theme-secondaryText">
          <code className="font-mono whitespace-pre">{code}</code>
        </pre>
        <button
          onClick={copy}
          className="absolute right-2 top-2 flex items-center gap-1 rounded-md border border-theme-chatDivider bg-theme-secondaryBackground px-2 py-1 text-xs font-medium text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground"
        >
          {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
};

// --- main component -----------------------------------------------------------

const EmbedInstaller = ({ apiKey }) => {
  const [platform, setPlatform] = useState("web");
  const [stackKey, setStackKey] = useState("react");
  // Per-placement options (theme/branding/features now come from admin config).
  const [position, setPosition] = useState("right");
  const [generated, setGenerated] = useState(false);

  const stacks = platform === "web" ? WEB : MOBILE;
  const active = stacks.find((s) => s.key === stackKey) || stacks[0];
  const key = apiKey || "YOUR_API_KEY";
  const showLoader = platform === "web" && !active.selfContained;

  // Any config change invalidates the shown snippet — user must re-generate.
  const switchPlatform = (p) => {
    setPlatform(p);
    setStackKey(p === "web" ? "react" : "reactnative");
    setGenerated(false);
  };
  const pickStack = (k) => {
    setStackKey(k);
    setGenerated(false);
  };

  const apply = (code) =>
    code
      .replace(/YOUR_API_KEY/g, key)
      // theme is configured in the admin panel now — drop it from the snippet
      .replace(/\n\s*theme:\s*"\{THEME\}",?/g, "")
      .replace(/&theme=\{THEME\}/g, "")
      // per-placement options
      .replace(/position:\s*"right"/g, `position: "${position}"`)
      .replace(/"\{CHANNEL_NAME\}"/g, '""')
      .replace(/channel=\{CHANNEL_NAME\}/g, "channel=")
      .trim();

  return (
    <div className="flex flex-col gap-8">
      <Field label="1. Where will you embed?">
        <Pills
          options={[
            { v: "web", l: "Web" },
            { v: "mobile", l: "Mobile" },
          ]}
          value={platform}
          onChange={switchPlatform}
        />
      </Field>

      <Field label="2. Your tech stack">
        <div className="flex flex-wrap gap-2">
          {stacks.map((s) => (
            <button
              key={s.key}
              onClick={() => pickStack(s.key)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                active.key === s.key
                  ? "bg-theme-accentSoft font-semibold text-theme-accent"
                  : "border border-theme-chatDivider text-theme-primaryText hover:bg-theme-tertiaryBackground"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="3. Placement options">
        <p className="-mt-1 mb-2 text-xs text-theme-placeholder">
          Theme, branding and features come from your admin settings. These just
          differ per embed placement.
        </p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-theme-secondaryText">Position</span>
          <Pills
            options={[
              { v: "right", l: "Right" },
              { v: "left", l: "Left" },
              { v: "center", l: "Center" },
              { v: "bottom", l: "Bottom" },
            ]}
            value={position}
            onChange={(v) => {
              setPosition(v);
              setGenerated(false);
            }}
          />
        </div>
      </Field>

      <div className="flex justify-end">
        <button
          onClick={() => setGenerated(true)}
          className="rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-semibold text-theme-accentText transition-colors hover:bg-theme-accentHover"
        >
          Generate snippet
        </button>
      </div>

      {generated && (
        <div className="flex flex-col gap-3 border-t border-theme-chatDivider pt-5">
          {showLoader && (
            <CodeBlock
              title="1. Load the widget (once, before &lt;/body&gt;)"
              code={loaderSnippet(key)}
            />
          )}
          <CodeBlock
            title={showLoader ? "2. Add a launch trigger" : "Snippet"}
            code={apply(active.code)}
          />
          {platform === "mobile" && MOBILE_NOTES[stackKey] && (
            <div className="rounded-lg border border-theme-chatDivider bg-theme-secondaryBackground p-3 text-xs text-theme-placeholder">
              <p className="mb-1.5">
                <span className="font-semibold text-theme-secondaryText">
                  Install:
                </span>{" "}
                <span className="font-mono">
                  {MOBILE_NOTES[stackKey].install}
                </span>
              </p>
              <ul className="list-disc space-y-1 pl-4">
                {MOBILE_NOTES[stackKey].tips.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          )}
          {!apiKey && (
            <p className="text-xs text-theme-warning">
              Verify your domain to generate your real API key — this snippet
              uses a placeholder for now.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EmbedInstaller;
